import {
  DrawerBody,
  DrawerFooter,
  DrawerContent,
  Drawer,
  DrawerHeader,
  Button,
  Card,
  Chip,
  CardHeader,
  CardBody,
  CardFooter,
  addToast,
  Radio,
  RadioGroup,
} from "@heroui/react";
import { api } from "@/lib/api-client";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useForm } from "@mantine/form";
import { getLocalizedValue, getTranslatedTimeframe } from "@/utils";
import { LimitationTimeframe, SubscriptionDurationUnit } from "@/server-types";
import { useEffect } from "react";
import { LimeDatePicker } from "@/Components/NextBase/LimeDatePicker";
import { CalendarDateTime, getLocalTimeZone } from "@internationalized/date";

const STEPS = ["subscription", "variation", "confirmation"] as const;

type CustomerSubscriptionDrawerProps = {
  isOpen: boolean;
  onOpenChange: () => void;
  customerId: number;
};

type CustomerSubscriptionFormValues = {
  currentStep: (typeof STEPS)[number];
  subscriptionId: number;
  variationId: number;
  isRecurring: boolean;
  validFrom: CalendarDateTime;
  durationValue: number;
  durationUnit: SubscriptionDurationUnit;
  priceCents: number;
  limitations: {
    timeframe: LimitationTimeframe;
    limitAmount: number;
  }[];
  subscriptionServices: {
    serviceId: number;
    limitations: {
      timeframe: LimitationTimeframe;
      limitAmount: number;
    }[];
  }[];
};

export function CustomerSubscriptionDrawer({
  isOpen,
  onOpenChange,
  customerId,
}: CustomerSubscriptionDrawerProps) {
  const { data: subscriptions = [] } = api.subscription.useGetSubscriptions({
    includeDeleted: false,
    includeInactive: false,
  });
  const createCustomerSubscription =
    api.customer.useCreateCustomerSubscription();

  const form = useForm<CustomerSubscriptionFormValues>({
    initialValues: {
      currentStep: "subscription",
      subscriptionId: 0,
      variationId: 0,
      isRecurring: false,
      validFrom: new CalendarDateTime(
        new Date().getFullYear(),
        new Date().getMonth() + 1,
        new Date().getDate(),
        new Date().getHours(),
        new Date().getMinutes(),
      ),
      durationValue: 0,
      durationUnit: "day",
      priceCents: 0,
      limitations: [],
      subscriptionServices: [],
    },
  });

  useEffect(() => {
    if (!isOpen) {
      form.reset();
    }
  }, [isOpen]);

  const handleSubscriptionChange = (subscriptionId: number) => {
    const subscription = subscriptions.find((s) => s.id === subscriptionId);
    if (!subscription) {
      addToast({
        title: t`Napaka`,
        description: t`Naročnina ni najdena`,
      });
      return;
    }
    form.setValues({
      ...form.values,
      subscriptionId,
      subscriptionServices: subscription.subscriptionServices,
      currentStep: "variation",
    });
  };

  const handleVariationChange = (variationId: number) => {
    const subscription = subscriptions.find(
      (s) => s.id === form.values.subscriptionId,
    );
    const variation = subscription?.subscriptionVariations.find(
      (v) => v.id === variationId,
    );

    if (!subscription || !variation) {
      addToast({
        color: "danger",
        title: t`Napaka`,
        description: t`Naročnina ali variacija ni najdena`,
      });
      return;
    }

    form.setValues({
      ...form.values,
      variationId,
      durationValue: variation.durationValue,
      durationUnit: variation.durationUnit,
      priceCents: variation.priceCents,
      isRecurring: variation.isRecurring,
      limitations: variation.limitations,
      currentStep: "confirmation",
    });
  };

  const handleSubmit = async (values: CustomerSubscriptionFormValues) => {
    try {
      const { currentStep, validFrom, ...submitValues } = values;
      await createCustomerSubscription.mutateAsync({
        body: {
          ...submitValues,
          validFrom: validFrom.toDate(getLocalTimeZone()),
          customerId,
        },
      });
      addToast({
        color: "success",
        title: t`Uspeh`,
        description: t`Naročnina je bila uspešno ustvarjena`,
      });
      onOpenChange();
      form.reset();
    } catch (error) {
      addToast({
        color: "danger",
        title: t`Napaka`,
        description: t`Prišlo je do napake pri ustvarjanju naročnine`,
      });
    }
  };

  const selectedSubscriptionDetails = subscriptions.find(
    (s) => s.id === form.values.subscriptionId,
  );

  const currentStepIndex = STEPS.indexOf(form.values.currentStep);
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === STEPS.length - 1;

  const goToNextStep = () => {
    if (!isLastStep) {
      form.setFieldValue("currentStep", STEPS[currentStepIndex + 1]);
    }
  };

  const goToPreviousStep = () => {
    if (!isFirstStep) {
      form.setFieldValue("currentStep", STEPS[currentStepIndex - 1]);
    }
  };

  const renderStepContent = () => {
    switch (form.values.currentStep) {
      case "subscription":
        return (
          <div className="flex flex-col gap-4">
            <Card
              isPressable
              isHoverable
              shadow="sm"
              className="w-full cursor-pointer border-2 border-dashed border-gray-300 hover:border-primary"
              onPress={() => {
                // TODO: Implement custom subscription flow
              }}
            >
              <CardBody>
                <h3 className="text-xl font-bold">
                  <Trans>Naročnina po meri</Trans>
                </h3>
                <p className="text-sm text-gray-600">
                  <Trans>
                    Ustvari naročnino po meri prilagojeno za stranko
                  </Trans>
                </p>
              </CardBody>
            </Card>

            {subscriptions.map((subscription) => (
              <Card
                key={subscription.id}
                isPressable
                isHoverable
                shadow="sm"
                className={`w-full cursor-pointer border-2 ${
                  form.values.subscriptionId === subscription.id
                    ? "border-primary"
                    : "border-transparent"
                }`}
                onPress={() => handleSubscriptionChange(subscription.id)}
              >
                <CardBody>
                  <h3 className="text-xl font-medium">
                    {getLocalizedValue(subscription.nameLocalized)}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {getLocalizedValue(subscription.descriptionLocalized)}
                  </p>
                  <p className="text-sm text-gray-600">
                    <Trans>Število variacij:</Trans>{" "}
                    <span className="font-bold">
                      {subscription.subscriptionVariations.length}
                    </span>
                  </p>
                </CardBody>
                <CardFooter>
                  <div className="flex flex-wrap gap-2">
                    {subscription.tags.map((tag) => (
                      <Chip radius="sm" key={tag.id} size="sm">
                        {getLocalizedValue(tag.nameLocalized)}
                      </Chip>
                    ))}
                  </div>
                </CardFooter>
              </Card>
            ))}
          </div>
        );

      case "variation":
        return (
          <div className="space-y-4">
            <div className="flex flex-col gap-4">
              {selectedSubscriptionDetails?.subscriptionVariations.map(
                (variation) => (
                  <Card
                    key={variation.id}
                    isPressable
                    shadow="sm"
                    isHoverable
                    className={`w-full cursor-pointer border-2 ${
                      form.values.variationId === variation.id
                        ? "border-primary"
                        : "border-transparent"
                    }`}
                    onPress={() => handleVariationChange(variation.id)}
                  >
                    <CardHeader className="flex justify-between">
                      <h3 className="text-lg font-medium">
                        {getLocalizedValue(variation.nameLocalized)}
                      </h3>
                      <p className="text-small text-gray-600">
                        {(variation.priceCents / 100).toFixed(2)}€
                      </p>
                    </CardHeader>
                    <CardBody>
                      <p className="text-small text-gray-600">
                        {t`Trajanje`}:{" "}
                        <span className="font-bold">
                          {variation.durationValue}{" "}
                          {getTranslatedTimeframe(variation.durationUnit)}
                        </span>
                      </p>
                      <p className="text-small text-gray-600">
                        {t`Omejitve`}: <Trans>Maksimalno</Trans>{" "}
                        {variation.limitations.map((limitation) => (
                          <span key={limitation.id} className="font-bold">
                            {limitation.limitAmount}x{" "}
                            {limitation.timeframe === "day" && t`na dan`}
                            {limitation.timeframe === "week" && t`na teden`}
                            {limitation.timeframe === "month" && t`na mesec`}
                            {limitation !==
                              variation.limitations[
                                variation.limitations.length - 1
                              ] && ", "}
                          </span>
                        ))}
                      </p>
                    </CardBody>
                    <CardFooter>
                      <div className="flex flex-wrap gap-2">
                        {variation.tags.map((tag) => (
                          <Chip radius="sm" key={tag.id} size="sm">
                            {getLocalizedValue(tag.nameLocalized)}
                          </Chip>
                        ))}
                      </div>
                    </CardFooter>
                  </Card>
                ),
              )}
            </div>
          </div>
        );

      case "confirmation":
        return (
          <div className="space-y-6">
            <Card shadow="sm">
              <div className="space-y-4 p-4">
                <div>
                  <h3 className="mb-2 text-lg font-medium">{t`Osnovne informacije`}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t`Naročnina`}</span>
                      <span className="font-medium">
                        {getLocalizedValue(
                          selectedSubscriptionDetails!.nameLocalized,
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t`Trajanje`}</span>
                      <span className="font-medium">
                        {form.getValues().durationValue}{" "}
                        {getTranslatedTimeframe(form.getValues().durationUnit)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t`Cena`}</span>
                      <span className="font-medium">
                        {(form.values.priceCents / 100).toFixed(2)}€
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <LimeDatePicker
                    label={t`Začetni datum`}
                    {...form.getInputProps("validFrom")}
                    hideTimeZone
                    className="max-w-xs"
                  />
                </div>

                <RadioGroup
                  size="sm"
                  orientation="horizontal"
                  value={
                    form.getValues().isRecurring ? "recurring" : "one-time"
                  }
                  onChange={(e) =>
                    form.setFieldValue(
                      "isRecurring",
                      e.target.value === "recurring",
                    )
                  }
                >
                  <Radio value="recurring">
                    <div className="flex flex-col">
                      <span>
                        <Trans>Ponavljajoča naročnina</Trans>
                      </span>
                      <span className="text-sm text-gray-500">
                        <Trans>
                          Naročnina se samodejno obnovi po izteku obdobja
                        </Trans>
                      </span>
                    </div>
                  </Radio>
                  <Radio value="one-time">
                    <div className="flex flex-col">
                      <span>
                        <Trans>Enkratna naročnina</Trans>
                      </span>
                      <span className="text-sm text-gray-500">
                        <Trans>Naročnina se ne obnovi samodejno</Trans>
                      </span>
                    </div>
                  </Radio>
                </RadioGroup>
              </div>
            </Card>
          </div>
        );
    }
  };

  const getStepTitle = () => {
    switch (form.values.currentStep) {
      case "subscription":
        return t`Izberi naročnino`;
      case "variation":
        return t`Izberi variacijo`;
      case "confirmation":
        return t`Pregled naročnine`;
    }
  };

  return (
    <Drawer isOpen={isOpen} onOpenChange={onOpenChange}>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <DrawerContent>
          {(onClose) => (
            <>
              <DrawerHeader>
                <div className="flex flex-col">
                  <Trans>Dodaj naročnino</Trans>
                  <p className="text-sm font-normal text-gray-500">
                    {getStepTitle()}
                  </p>
                </div>
              </DrawerHeader>

              <DrawerBody>{renderStepContent()}</DrawerBody>

              <DrawerFooter>
                <Button
                  variant="bordered"
                  type="button"
                  onPress={isFirstStep ? onClose : goToPreviousStep}
                >
                  {isFirstStep ? t`Prekliči` : t`Nazaj`}
                </Button>
                <Button
                  key={form.values.currentStep}
                  type={isLastStep ? "submit" : "button"}
                  color="primary"
                  onPress={goToNextStep}
                  data-identifier="add-customer-subscription-submit-button"
                  isDisabled={
                    (form.values.currentStep === "subscription" &&
                      !form.values.subscriptionId) ||
                    (form.values.currentStep === "variation" &&
                      !form.values.variationId)
                  }
                >
                  {isLastStep ? t`Dodaj naročnino` : t`Nadaljuj`}
                </Button>
              </DrawerFooter>
            </>
          )}
        </DrawerContent>
      </form>
    </Drawer>
  );
}
